import {match} from 'ts-pattern';

import type {CategoryItem} from '../../../../../hooks/useFilterCategorySelection';
import {filterCategoriesBySearch} from '../../FilterContentArea/utils/filterCategoriesBySearch';

type GetExpandedIdsForSearchChangeParams = {
  previousTerm: string;
  nextTerm: string;
  /** Sections currently expanded. */
  expandedIds: Set<string>;
  /** Expansion captured when the current search started; null outside a search. */
  snapshot: Set<string> | null;
  categories: CategoryItem[];
};

type ExpandedIdsForSearchChange = {
  expandedIds: Set<string>;
  snapshot: Set<string> | null;
};

const getMatchingIds = (categories: CategoryItem[], term: string): string[] =>
  filterCategoriesBySearch(categories, term).map((category) => category.id);

const union = (base: Set<string>, ids: string[]): Set<string> => {
  const next = new Set(base);
  ids.forEach((id) => next.add(id));
  return next;
};

/**
 * Accordion expansion for one filter-search keystroke. Typing opens the
 * sections the term surfaces so their widgets are reachable without a click;
 * clearing the term restores the expansion the user had before searching.
 *
 * - search starts: snapshot the current expansion, expand every match;
 * - refining: expand only the NEWLY matching sections, so a section the user
 *   collapsed mid-search (or "Collapse") is not reopened by the next keystroke;
 * - cleared: restore the snapshot (toggles made while searching are discarded);
 * - no search on either side: unchanged.
 */
export const getExpandedIdsForSearchChange = (
  params: GetExpandedIdsForSearchChangeParams
): ExpandedIdsForSearchChange =>
  match({wasSearching: params.previousTerm.length > 0, isSearching: params.nextTerm.length > 0})
    .with({wasSearching: false, isSearching: true}, () => ({
      expandedIds: union(params.expandedIds, getMatchingIds(params.categories, params.nextTerm)),
      snapshot: params.expandedIds,
    }))
    .with({wasSearching: true, isSearching: true}, () => {
      const previousIds = new Set(getMatchingIds(params.categories, params.previousTerm));
      const newIds = getMatchingIds(params.categories, params.nextTerm).filter(
        (id) => !previousIds.has(id)
      );
      return {expandedIds: union(params.expandedIds, newIds), snapshot: params.snapshot};
    })
    .with({wasSearching: true, isSearching: false}, () => ({
      expandedIds: params.snapshot ?? params.expandedIds,
      snapshot: null,
    }))
    .otherwise(() => ({expandedIds: params.expandedIds, snapshot: params.snapshot}));
